import { FetchError } from '@src/utils/fetch-error';
import { fetchWithRetry, GetS3SignedUrl, S3SignedUrlFetch } from '@src/utils/fetch-utils';

const LOAD_RETRIES = 2;
const LOAD_BACKOFF_MS = 500;
const PROBE_CONCURRENCY = 4;
const WORKBOOK_CHUNK_BYTES = 2 * 1024 * 1024;

export type GeoMmmLoadStep = 'existence check' | 'workbook download';

export class GeoMmmLoadError extends Error {
  constructor(
    public step: GeoMmmLoadStep,
    public path: string,
    public status: number | undefined,
    cause?: unknown
  ) {
    super(
      status === undefined
        ? `Geo MMM ${step} failed for ${path}: network error`
        : `Geo MMM ${step} failed for ${path}: status ${status}`,
      { cause }
    );
    this.name = 'GeoMmmLoadError';
  }
}

export const isTransientGeoMmmLoadError = (e: unknown): e is GeoMmmLoadError =>
  e instanceof GeoMmmLoadError && (e.status === undefined || e.status >= 500);

// Retries network errors and 5xx. Any other status is for the caller to judge, so a 404 is never
// retried. A 4xx from the signer is not retried either; it surfaces as a non-transient error.
const withGeoMmmRetry = async <T>(
  path: string,
  step: GeoMmmLoadStep,
  attempt: () => Promise<T>
): Promise<T> => {
  try {
    const outcome = await fetchWithRetry<{ value: T } | { failure: unknown }>(
      async () => {
        try {
          return { value: await attempt() };
        } catch (e) {
          // Returned, not thrown, so fetchWithRetry does not retry a client error or a bug.
          const retryable =
            e instanceof GeoMmmLoadError ||
            e instanceof TypeError ||
            (e instanceof FetchError && e.status >= 500);
          if (!retryable) return { failure: e };
          throw e;
        }
      },
      { retries: LOAD_RETRIES, backoffMs: LOAD_BACKOFF_MS, backoffMultiplier: 2 }
    );
    if ('failure' in outcome) throw outcome.failure;
    return outcome.value;
  } catch (e) {
    if (e instanceof GeoMmmLoadError) throw e;
    if (e instanceof FetchError || e instanceof TypeError) {
      throw new GeoMmmLoadError(step, path, e instanceof FetchError ? e.status : undefined, e);
    }
    throw e;
  }
};

export const fetchGeoMmmArtifact = (
  path: string,
  step: GeoMmmLoadStep,
  options: RequestInit = {}
): Promise<Response> =>
  withGeoMmmRetry(path, step, async () => {
    const res: Response = await S3SignedUrlFetch(path, options);
    if (res.status >= 500) {
      throw new GeoMmmLoadError(step, path, res.status);
    }
    return res;
  });

// The body is read inside the retry: a connection cut mid-body rejects arrayBuffer() with a
// TypeError after a 200, which a retry around the fetch alone never sees.
const readGeoMmmRange = (url: string, path: string, start: number) =>
  withGeoMmmRetry(path, 'workbook download', async () => {
    const res = await fetch(url, {
      headers: { Range: `bytes=${start}-${start + WORKBOOK_CHUNK_BYTES - 1}` }
    });
    if (res.status >= 500) {
      throw new GeoMmmLoadError('workbook download', path, res.status);
    }
    return { status: res.status, bytes: res.ok ? await res.arrayBuffer() : undefined };
  });

// Ranged reads so a cut connection only repeats one chunk, not the whole file. The bucket does not
// expose Content-Range, so the end is the first 416; the next range starts where the last chunk
// actually ended, so a short chunk can never pass for the end of the file. A server that ignores
// Range answers 200 with the whole file, which is only believable for the first chunk.
export const downloadGeoMmmWorkbook = async (path: string): Promise<ArrayBuffer> => {
  const url = await withGeoMmmRetry(path, 'workbook download', () => GetS3SignedUrl(path));
  const chunks: ArrayBuffer[] = [];
  let start = 0;
  for (;;) {
    const { status, bytes } = await readGeoMmmRange(url, path, start);
    if (status === 416 && start > 0) break;
    if (!bytes) {
      throw new Error(`Failed to fetch data from URL: ${path} - Status: ${status}`);
    }
    if (status === 200) {
      if (start > 0) throw new GeoMmmLoadError('workbook download', path, undefined);
      return bytes;
    }
    if (!bytes.byteLength) break;
    chunks.push(bytes);
    start += bytes.byteLength;
  }
  return new Blob(chunks).arrayBuffer();
};

// The presigned URL is signed for GET, not HEAD, so probe with a ranged GET. Only a transient
// failure keeps the run, so the load itself can report it; a definite client error means missing.
export const geoMmmRunExists = async (path: string): Promise<boolean> => {
  try {
    const res = await fetchGeoMmmArtifact(path, 'existence check', {
      headers: { Range: 'bytes=0-0' }
    });
    return res.ok;
  } catch (e) {
    return isTransientGeoMmmLoadError(e);
  }
};

const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await fn(items[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};

export const probeGeoMmmRuns = (paths: string[]): Promise<boolean[]> =>
  mapWithConcurrency(paths, PROBE_CONCURRENCY, geoMmmRunExists);
